import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { chatApi } from '@/api/chat';
import type { Chat, Prospect } from '@/types/chat';

interface EmailNotificationFormProps {
  open: boolean;
  onClose: () => void;
  chat: Chat | null;
  currentUser: {
    name: string;
    email: string;
  };
}

interface EmailFormData {
  fromEmail: string;
  fromName: string;
  to: string;
  cc: string[];
  subject: string;
  message: string;
  chatUuid?: string;
}

interface MessageContentFields {
  campaign: string;
  conversationBrief: string;
  prospectName: string;
  title: string;
  company: string;
  linkedin: string;
  location: string;
  email: string;
  date: string;
  time: string;
  conversation: string;
  projectOwnerName: string;
}

interface FormErrors {
  fromEmail?: string;
  fromName?: string;
  to?: string;
  cc?: string;
  subject?: string;
  message?: string;
  chatUuid?: string;
}

export const EmailNotificationForm: React.FC<EmailNotificationFormProps & { onSuccess?: (msg?: string) => void }> = ({
  open,
  onClose,
  chat,
  currentUser,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<EmailFormData>({
    fromEmail: '',
    fromName: '',
    to: '',
    cc: [],
    subject: '',
    message: '',
  });
  const [ccInput, setCcInput] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [contentFields, setContentFields] = useState<MessageContentFields | null>(null);

  // Helper function to get prospect display name
  const getProspectDisplayName = (prospect: Prospect | null | undefined): string => {
    if (!prospect) return 'Prospect';
    return prospect.full_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Prospect';
  };

  // Helper function to get prospect LinkedIn URL
  const getProspectLinkedInUrl = (prospect: Prospect | null | undefined): string => {
    if (!prospect) return 'Not provided';
    return prospect.linkedin_public_url || prospect.linkedin_url || 'Not provided';
  };

  // Update form when chat changes
  useEffect(() => {
    if (chat) {
      const prospectName = getProspectDisplayName(chat.prospect);
      const prospectCompany = chat.prospect?.current_company?.name || 'Company';

      const subject = `Prospect Meeting: ${prospectName}-${prospectCompany}`;

      // Generate message content
      const stream = chat.stream;
      const prospect = chat.prospect;
      const meetingTime = chat.meeting_time || 'Not specified';
      const chatEmail = chat.email || prospect?.email || 'Not provided';
      const chatBrief = chat.brief || 'No brief available';
      const chatHistory = chat.chat_history || 'No conversation history';

      const ownerName = chat.owner?.name || 'Owner';

      const initialContent: MessageContentFields = {
        campaign: stream?.name || 'Not specified',
        conversationBrief: chatBrief,
        prospectName: getProspectDisplayName(prospect) || 'Not specified',
        title: prospect?.title || 'Not specified',
        company: prospect?.current_company?.name || 'Not specified',
        linkedin: getProspectLinkedInUrl(prospect),
        location: prospect?.location || 'Not specified',
        email: chatEmail,
        date: meetingTime,
        time: meetingTime,
        conversation: chatHistory,
        projectOwnerName: ownerName,
      };

      const buildMessage = (c: MessageContentFields, senderName: string) => `Dear ${c.projectOwnerName},<br /><br />
I hope you are doing well.<br /><br />
Please coordinate with this prospect to arrange a suitable date and time for the meeting. <br /><br />
<strong>Campaign: </strong>${c.campaign}<br />
<strong>Conversation brief: </strong>${c.conversationBrief}<br />
<strong>Name: </strong>${c.prospectName}<br />
<strong>Title: </strong>${c.title}<br />
<strong>Company: </strong>${c.company}<br />
<strong>LinkedIn: </strong>${c.linkedin}<br />
<strong>Location: </strong>${c.location}<br />
<strong>Email: </strong>${c.email}<br />
<strong>Date: </strong>${c.date}<br />
<strong>Time: </strong>${c.time}<br />
<strong>Conversation:</strong><br />
<blockquote style="white-space: pre-wrap;border-left: solid;padding-left: 1ex;border-color: #e8e8e8;background-color: whitesmoke;">
    ${c.conversation}
</blockquote><br /><br />

If you have any questions or concerns, please let me know. <br /><br />

Kind regards,<br />

${senderName}`;

      setContentFields(initialContent);

      const message = buildMessage(initialContent, currentUser.name);

      setFormData({
        fromEmail: currentUser.email,
        fromName: currentUser.name,
        to: chat.owner?.email || '',
        cc: [],
        subject,
        message,
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [chat, currentUser]);

  // Detect changes
  useEffect(() => {
    if (chat) {
      const prospectName = getProspectDisplayName(chat.prospect);
      const prospectCompany = chat.prospect?.current_company?.name || 'Company';
      const subject = `Prospect Meeting: ${prospectName}-${prospectCompany}`;

      const hasModifications =
        formData.fromEmail !== currentUser.email ||
        formData.fromName !== currentUser.name ||
        formData.to !== (chat.owner?.email || '') ||
        formData.subject !== subject ||
        formData.message !== '';
      setHasChanges(hasModifications);
    }
  }, [formData, chat]);

  // Validation function
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.fromEmail.trim()) {
      newErrors.fromEmail = 'From email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail)) {
      newErrors.fromEmail = 'Please enter a valid email address';
    }

    if (!formData.fromName.trim()) {
      newErrors.fromName = 'From name is required';
    }

    if (!formData.to.trim()) {
      newErrors.to = 'Project owner email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.to)) {
      newErrors.to = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    // Validate CC emails
    const invalidCcEmails = formData.cc.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidCcEmails.length > 0) {
      newErrors.cc = 'Please enter valid email addresses';
    }

    return newErrors;
  };

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: (data: EmailFormData) =>
      chatApi.sendEmailNotification(data),
    onSuccess: () => {
      // notify parent
      if (onSuccess) {
        onSuccess('Email notification sent successfully');
      }
      onClose();
      setFormData({
        fromEmail: '',
        fromName: '',
        to: '',
        cc: [],
        subject: '',
        message: '',
      });
      setCcInput('');
      setHasChanges(false);
      setErrors({});
    },
    onError: (error: any) => {
      console.error('Failed to send email:', error);
    },
  });

  // Reset previous error state when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setCcInput('');
      if (sendEmailMutation.reset) {
        sendEmailMutation.reset();
      }
    } else {
      setErrors({});
      if (sendEmailMutation.reset) {
        sendEmailMutation.reset();
      }
    }
  }, [open]);

  // Event handlers
  const handleInputChange = (field: keyof EmailFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCcInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCcInput(event.target.value);
  };

  const handleCcKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addCcEmail();
    }
  };

  const addCcEmail = () => {
    const email = ccInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !formData.cc.includes(email)) {
      setFormData(prev => ({ ...prev, cc: [...prev.cc, email] }));
      setCcInput('');
      if (errors.cc) {
        setErrors(prev => ({ ...prev, cc: undefined }));
      }
    }
  };

  // Compose message when content fields change
  const handleContentFieldChange = (field: keyof MessageContentFields) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!contentFields) return;
    const value = event.target.value;
    const updated: MessageContentFields = { ...contentFields, [field]: value } as MessageContentFields;
    setContentFields(updated);

    const message = `Dear ${updated.projectOwnerName},<br /><br />
I hope you are doing well.<br /><br />
Please coordinate with this prospect to arrange a suitable date and time for the meeting. <br /><br />
<strong>Campaign: </strong>${updated.campaign}<br />
<strong>Conversation brief: </strong>${updated.conversationBrief}<br />
<strong>Name: </strong> ${updated.prospectName}<br />
<strong>Title: </strong> ${updated.title}<br />
<strong>Company: </strong> ${updated.company}<br />
<strong>LinkedIn: </strong> ${updated.linkedin}<br />
<strong>Location: </strong> ${updated.location}<br />
<strong>Email: </strong> ${updated.email}<br />
<strong>Date: </strong> ${updated.date}<br />
<strong>Time: </strong> ${updated.time}<br />
<strong>Conversation:</strong><br />
<blockquote style="white-space: pre-wrap;border-left: solid;padding-left: 1ex;border-color: #e8e8e8;background-color: whitesmoke;">
    ${updated.conversation}
</blockquote><br /><br />

If you have any questions or concerns, please let me know. <br /><br />

Kind regards,<br />

${formData.fromName || currentUser.name}`;
    setFormData(prev => ({ ...prev, message }));
  };

  // Re-compose message when sender name changes
  useEffect(() => {
    if (!contentFields) return;
    const message = `Dear ${contentFields.projectOwnerName},<br /><br />
I hope you are doing well.<br /><br />
Please coordinate with this prospect to arrange a suitable date and time for the meeting. <br /><br />
<strong>Campaign: </strong>${contentFields.campaign}<br />
<strong>Conversation brief: ${contentFields.conversationBrief}<br />
<strong>Name: </strong>${contentFields.prospectName}<br />
<strong>Title: </strong>${contentFields.title}<br />
<strong>Company: </strong>${contentFields.company}<br />
<strong>LinkedIn: </strong>${contentFields.linkedin}<br />
<strong>Location: </strong>${contentFields.location}<br />
<strong>Email: </strong>${contentFields.email}<br />
<strong>Date: </strong>${contentFields.date}<br />
<strong>Time: </strong>${contentFields.time}<br />
<strong>Conversation:</strong><br />
<blockquote style="white-space: pre-wrap;border-left: solid;padding-left: 1ex;border-color: #e8e8e8;background-color: whitesmoke;">
    ${contentFields.conversation}
</blockquote><br /><br />

If you have any questions or concerns, please let me know. <br /><br />

Kind regards,<br />

${formData.fromName || currentUser.name}`;
    setFormData(prev => ({ ...prev, message }));
  }, [formData.fromName, contentFields]);

  const removeCcEmail = (emailToRemove: string) => {
    setFormData(prev => ({ ...prev, cc: prev.cc.filter(email => email !== emailToRemove) }));
  };

  const handleSend = () => {
    if (!hasChanges) return;

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    sendEmailMutation.mutate({
      ...formData,
      chatUuid: chat?.key,
    });
  };

  const handleClose = () => {
    if (!sendEmailMutation.isPending) {
      onClose();
    }
  };

  if (!chat) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Send Meeting Notification
        </Typography>
        <IconButton onClick={handleClose} disabled={sendEmailMutation.isPending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ pt:1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* From Name Field */}
          <TextField
            fullWidth
            label="From Name"
            variant="outlined"
            value={formData.fromName}
            onChange={handleInputChange('fromName')}
            disabled={sendEmailMutation.isPending}
            error={!!errors.fromName}
            helperText={errors.fromName || "Sender's name"}
            required
          />

          {/* From Email Field */}
          <TextField
            fullWidth
            label="From Email"
            variant="outlined"
            value={formData.fromEmail}
            onChange={handleInputChange('fromEmail')}
            disabled={sendEmailMutation.isPending}
            error={!!errors.fromEmail}
            helperText={errors.fromEmail || "Sender's email address"}
            required
          />

          {/* To Field */}
          <TextField
            fullWidth
            label="To"
            variant="outlined"
            value={formData.to}
            onChange={handleInputChange('to')}
            disabled={sendEmailMutation.isPending}
            error={!!errors.to}
            helperText={errors.to || "Project owner's email address"}
            required
          />

          {/* CC Field */}
          <Box>
            <TextField
              fullWidth
              label="CC"
              variant="outlined"
              value={ccInput}
              onChange={handleCcInputChange}
              onKeyPress={handleCcKeyPress}
              disabled={sendEmailMutation.isPending}
              error={!!errors.cc}
              helperText={errors.cc || "Press Enter or comma to add email addresses"}
              placeholder="Enter email addresses (press Enter or comma to add)"
            />
            {formData.cc.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {formData.cc.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => removeCcEmail(email)}
                    disabled={sendEmailMutation.isPending}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Subject Field */}
          <TextField
            fullWidth
            label="Subject"
            variant="outlined"
            value={formData.subject}
            onChange={handleInputChange('subject')}
            disabled={sendEmailMutation.isPending}
            error={!!errors.subject}
            helperText={errors.subject || "Email subject line"}
            required
          />

          {/* Message Content Fields (compose message) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Campaign" value={contentFields?.campaign || ''} onChange={handleContentFieldChange('campaign')} disabled={sendEmailMutation.isPending} />
            <TextField label="Conversation brief" value={contentFields?.conversationBrief || ''} onChange={handleContentFieldChange('conversationBrief')} disabled={sendEmailMutation.isPending} />
            <TextField label="Prospect name" value={contentFields?.prospectName || ''} onChange={handleContentFieldChange('prospectName')} disabled={sendEmailMutation.isPending} />
            <TextField label="Title" value={contentFields?.title || ''} onChange={handleContentFieldChange('title')} disabled={sendEmailMutation.isPending} />
            <TextField label="Company" value={contentFields?.company || ''} onChange={handleContentFieldChange('company')} disabled={sendEmailMutation.isPending} />
            <TextField label="LinkedIn" value={contentFields?.linkedin || ''} onChange={handleContentFieldChange('linkedin')} disabled={sendEmailMutation.isPending} />
            <TextField label="Location" value={contentFields?.location || ''} onChange={handleContentFieldChange('location')} disabled={sendEmailMutation.isPending} />
            <TextField label="Email" value={contentFields?.email || ''} onChange={handleContentFieldChange('email')} disabled={sendEmailMutation.isPending} />
            <TextField label="Date" value={contentFields?.date || ''} onChange={handleContentFieldChange('date')} disabled={sendEmailMutation.isPending} />
            <TextField label="Time" value={contentFields?.time || ''} onChange={handleContentFieldChange('time')} disabled={sendEmailMutation.isPending} />
            <TextField label="Conversation" value={contentFields?.conversation || ''} onChange={handleContentFieldChange('conversation')} disabled={sendEmailMutation.isPending} multiline minRows={4} />
          </Box>

          {/* Message Field */}
          <TextField
            fullWidth
            label="Message"
            variant="outlined"
            multiline
            rows={15}
            value={formData.message}
            onChange={handleInputChange('message')}
            disabled={sendEmailMutation.isPending}
            error={!!errors.message}
            helperText={errors.message || "Email message content"}
            placeholder="Enter your message..."
            required
          />
        </Box>

        {/* Error Display */}
        {sendEmailMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to send email. Please try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={sendEmailMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            sendEmailMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
          onClick={handleSend}
          disabled={!hasChanges || sendEmailMutation.isPending}
          sx={{ minWidth: 120 }}
        >
          {sendEmailMutation.isPending ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
